/* External */
import { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { useLocation } from 'react-router-dom';

/* Components */
import CatalogLiveCard from './CatalogLiveCard';
import CatalogVodCard from './CatalogVodCard';
import { ScrollPosition } from '../ScrollView';

/* Utils */
import { isInNLast, noOp } from '../../utils/functions';
import { formatPlaySource, transmissionContentType } from '../../utils/googleTags';

/* Styles */
import './CatalogAll.scss';

function CatalogAll({
  className,
  cardsPerRow,
  items,
  style,
  onCardArrowPress,
  focusFirstCard,
  hideMetadata,
  dark,
  type,
  catalogTitle,
  pdpCommonTagEvent,
  myListCommonTagEvent,
}) {
  const { state: useLocationState, pathname } = useLocation();
  const isMyList = className.includes('my-list');
  const isShow = pathname.includes('detail/shows');

  const locationState = useCallback(({ content }) => ({
    transmissionType: transmissionContentType(content?.type || content?.schema),
    sectionName: isMyList ? 'my-list' : (useLocationState?.carouselTitle || useLocationState?.sectionName),
    componentType: isMyList ? 'my-list' : 'catalog-collection',
    collectionName: isMyList ? '' : catalogTitle,
    gtmPlaySource: isMyList ? 'my-list' : (formatPlaySource({
      parentLander: useLocationState?.parentLander,
      section: useLocationState?.carouselTitle,
    }) || useLocationState?.gtmPlaySource),
  }), [
    catalogTitle,
    isMyList,
    useLocationState?.carouselTitle,
    useLocationState?.gtmPlaySource,
    useLocationState?.parentLander,
    useLocationState?.sectionName,
  ]);

  const cards = useMemo(() => {
    const length = items.size;
    const numberOfCardsInLastLine = (length % cardsPerRow) || cardsPerRow;

    return items.map((content, index) => {
      const isInFirstLine = index < cardsPerRow;
      const isInLastLine = isInNLast(index, numberOfCardsInLastLine, length);
      const blockScroll = (isInFirstLine || isInLastLine)
        ? ScrollPosition.END
        : ScrollPosition.CENTER;
      const isLastCard = index === length - 1;
      if (type === 'LIVE') {
        return (
          <CatalogLiveCard
            index={index}
            key={content.vrioAssetId}
            width={`${100 / cardsPerRow}%`}
            blockScroll={blockScroll}
            content={content}
            position={index}
            focusFirstCard={focusFirstCard}
            isLastCard={isLastCard}
            handleArrowPress={onCardArrowPress}
            hideMetadata={hideMetadata}
            dark={dark}
            locationState={locationState}
          />
        );
      }

      return (
        <CatalogVodCard
          index={index}
          key={content.vrioAssetId}
          width={`${100 / cardsPerRow}%`}
          blockScroll={blockScroll}
          content={content}
          position={index}
          focusFirstCard={focusFirstCard}
          isLastCard={isLastCard}
          handleArrowPress={onCardArrowPress}
          hideMetadata={hideMetadata}
          dark={dark}
          locationState={locationState}
          pdpCommonTagEvent={pdpCommonTagEvent}
          isShow={isShow}
          myListCommonTagEvent={myListCommonTagEvent}
          isMyList={isMyList}
        />
      );
    });
  }, [
    cardsPerRow,
    dark,
    focusFirstCard,
    hideMetadata,
    items,
    locationState,
    onCardArrowPress,
    type,
    pdpCommonTagEvent,
    isShow,
    myListCommonTagEvent,
    isMyList,
  ]);

  return (
    <div className={`dtv-html5-c-catalog-all ${className}`} style={style}>
      {cards}
    </div>
  );
}

CatalogAll.propTypes = {
  className: PropTypes.string,
  focusFirstCard: PropTypes.bool,
  hideMetadata: PropTypes.bool,
  dark: PropTypes.bool,
  style: PropTypes.shape({}),
  cardsPerRow: PropTypes.number.isRequired,
  items: PropTypes.instanceOf(List),
  onCardArrowPress: PropTypes.func,
  type: PropTypes.string,
  catalogTitle: PropTypes.string,
  pdpCommonTagEvent: PropTypes.func,
  myListCommonTagEvent: PropTypes.func,
};

CatalogAll.defaultProps = {
  className: '',
  style: undefined,
  focusFirstCard: false,
  hideMetadata: false,
  dark: false,
  items: [],
  onCardArrowPress: undefined,
  type: '',
  catalogTitle: '',
  pdpCommonTagEvent: noOp,
  myListCommonTagEvent: noOp,
};

export default memo(withFocusable()(memo(CatalogAll)));
